import {ITEM_TYPE} from './constants'

// ITEM_TYPE doubles as the server's item `kind` (0 question, 1 content screen,
// 2 skip-logic). Builder entries keep an `itemType` field with that value and an
// `itemId` - the integer item-row id the server addresses items by. Unsaved
// items have itemId null until they are saved.
// Builder entries carry `itemType`; items from the respondent read carry `kind`.
// Both hold the same value, so read whichever is present.
export const isContentScreen = entry =>
  (entry?.itemType ?? entry?.kind) === ITEM_TYPE.CONTENT

export const isSkipLogicSection = entry =>
  (entry?.itemType ?? entry?.kind) === ITEM_TYPE.SKIP_LOGIC

const isNonQuestion = entry =>
  isContentScreen(entry) || isSkipLogicSection(entry)

// Unique per entry: non-question items by their item id, questions by their
// question id, never colliding. Falls back to itemKey for the respondent runner,
// which still reads the older item shape until it moves to the real API.
export const entryKey = entry =>
  isNonQuestion(entry)
    ? `item:${entry.itemId ?? entry.id ?? entry.itemKey}`
    : entry?.id

// One ordered builder list from the server's item list and the full questions.
// The item list (GetSurveyItemsBySurveyId) is ordered and carries each item's
// id/kind/order, plus content/rules for the non-question kinds, but only a
// questionId for questions - so question rows are filled in from `questions`,
// matched on questionId, and tagged with their item id and order. Without an
// item list the questions come back as they are.
export const mergeSurveyItems = (items, questions) => {
  const qs = Array.isArray(questions) ? questions : []
  if (!Array.isArray(items) || items.length === 0) return qs

  const byId = new Map(qs.map(q => [q.id, q]))
  const placed = new Set()
  const merged = []
  items.forEach(item => {
    if (item?.kind === ITEM_TYPE.QUESTION) {
      const question = byId.get(item.questionId)
      if (question && !placed.has(question.id)) {
        placed.add(question.id)
        merged.push({...question, itemId: item.id, order: item.order})
      }
    } else if (
      item?.kind === ITEM_TYPE.CONTENT ||
      item?.kind === ITEM_TYPE.SKIP_LOGIC
    ) {
      merged.push({...item, itemType: item.kind, itemId: item.id})
    }
  })
  qs.filter(q => !placed.has(q.id)).forEach(q => merged.push(q))
  return merged
}

// Builder entries, re-keyed for the preview's route evaluator. resolveRoute /
// explainRoute / projectForPersona read `id` and `kind` and address questions by
// their item id, so each entry's item id becomes `id` and its itemType becomes
// `kind`, while every render field (questionType, staffText, content, rules) is
// carried through untouched. The original question id is kept as questionId so an
// answer recorded under either id still lines up.
//
// The evaluator reads a question's choice options from `item.options` ({value,
// order, numValue}) to turn a Likert answer into a number for numeric operators,
// but builder questions keep their options under `multiOptionResponseAttributes`;
// they are mapped across here so Likert eq/gt/between rules evaluate the same way
// they do on the server.
export const toPreviewItems = entries =>
  (Array.isArray(entries) ? entries : []).map((entry, i) => ({
    ...entry,
    // An unsaved item has no item id yet; a synthetic negative id keeps it
    // distinct from its neighbours (so stepping stays correct) and can never
    // collide with a real, positive id a rule might target.
    id: entry.itemId ?? entry.id ?? -(i + 1),
    kind: entry.itemType ?? entry.kind ?? ITEM_TYPE.QUESTION,
    questionId: entry.questionId ?? entry.id,
    options:
      entry.options ??
      (Array.isArray(entry.multiOptionResponseAttributes)
        ? entry.multiOptionResponseAttributes.map(o => ({
            value: o.value,
            order: o.order,
            numValue: o.numValue,
          }))
        : undefined),
  }))

// The shape the skip-logic helpers read: an ordered item list keyed by itemId,
// with each item's type and (for sections) its rules.
export const toEngineSurvey = entries => ({
  items: (Array.isArray(entries) ? entries : []).map(entry => ({
    itemId: entry.itemId,
    itemType: entry.itemType ?? ITEM_TYPE.QUESTION,
    rules: entry.rules,
  })),
})
